// Walkability grid + A* with string-pulling. Obstacles are pre-inflated by the hero radius.
// Ported verbatim from prototypes/match/js/nav.js (already framework-free).

export type Point = { x: number; z: number };

export type NavGridOpts = {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  cell: number;
  blocked: (x: number, z: number) => boolean;
};

export class NavGrid {
  minX: number;
  minZ: number;
  cell: number;
  w: number;
  h: number;
  walk: Uint8Array;
  readonly region: Int32Array;

  constructor({ minX, maxX, minZ, maxZ, cell, blocked }: NavGridOpts) {
    this.minX = minX;
    this.minZ = minZ;
    this.cell = cell;
    this.w = Math.ceil((maxX - minX) / cell);
    this.h = Math.ceil((maxZ - minZ) / cell);
    this.walk = new Uint8Array(this.w * this.h);
    for (let iz = 0; iz < this.h; iz++) {
      for (let ix = 0; ix < this.w; ix++) {
        const x = minX + (ix + 0.5) * cell, z = minZ + (iz + 0.5) * cell;
        this.walk[iz * this.w + ix] = blocked(x, z) ? 0 : 1;
      }
    }
    this.region = this.labelRegions();
  }

  ix(x: number) { return Math.floor((x - this.minX) / this.cell); }
  iz(z: number) { return Math.floor((z - this.minZ) / this.cell); }
  cx(ix: number) { return this.minX + (ix + 0.5) * this.cell; }
  cz(iz: number) { return this.minZ + (iz + 0.5) * this.cell; }

  ok(ix: number, iz: number) { return ix >= 0 && iz >= 0 && ix < this.w && iz < this.h && this.walk[iz * this.w + ix] === 1; }
  isWalkable(x: number, z: number) { return this.ok(this.ix(x), this.iz(z)); }

  nearestWalkable(x: number, z: number): Point {
    const ix = this.ix(x), iz = this.iz(z);
    if (this.ok(ix, iz)) return { x, z };
    for (let r = 1; r < 40; r++) {
      let best: Point | null = null, bestD = Infinity;
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== r || !this.ok(ix + dx, iz + dz)) continue;
          const px = this.cx(ix + dx), pz = this.cz(iz + dz), d = (px - x) ** 2 + (pz - z) ** 2;
          if (d < bestD) { bestD = d; best = { x: px, z: pz }; }
        }
      }
      if (best) return best;
    }
    return { x, z };
  }

  lineWalkable(ax: number, az: number, bx: number, bz: number) {
    const len = Math.hypot(bx - ax, bz - az), steps = Math.ceil(len / (this.cell * 0.5));
    for (let i = 0; i <= steps; i++) {
      const k = steps ? i / steps : 0;
      if (!this.isWalkable(ax + (bx - ax) * k, az + (bz - az) * k)) return false;
    }
    return true;
  }

  /* Returns waypoints [{x, z}] from a to b (excluding a), or [] if unreachable. */
  findPath(ax: number, az: number, bx: number, bz: number): Point[] {
    const s = this.nearestWalkable(ax, az), g = this.nearestWalkable(bx, bz);
    if (this.lineWalkable(s.x, s.z, g.x, g.z)) return [g];
    const W = this.w, H = this.h, walk = this.walk;
    const six = this.ix(s.x), siz = this.iz(s.z), gix = this.ix(g.x), giz = this.iz(g.z);
    const start = siz * W + six, goal = giz * W + gix;
    // Different connected regions: A* would flood the whole region and still return [] (the DoS case).
    // Only when both cells are real walkable cells; any other start/goal falls through to the full search.
    if (this.ok(six, siz) && this.ok(gix, giz) && this.region[start] !== this.region[goal]) return [];
    const gx = goal % W, gz = (goal / W) | 0;
    // Scratch arrays are shared by every call; a cell's gScore/came is valid only if seen[cell] === gen,
    // and it is closed only if closedAt[cell] === gen — this replaces refilling three grid-sized arrays.
    const sc = this.scratch(), { gScore, came, seen, closedAt, heap } = sc;
    const gen = ++sc.gen;
    const hEst = (i: number) => {
      const dx = Math.abs((i % W) - gx), dz = Math.abs(((i / W) | 0) - gz);
      return (dx + dz) + (Math.SQRT2 - 2) * Math.min(dx, dz);
    };
    heap.clear();
    seen[start] = gen; gScore[start] = 0; came[start] = -1;
    heap.push(start, hEst(start));
    while (heap.size) {
      const cur = heap.pop();
      if (cur === goal) break;
      if (closedAt[cur] === gen) continue;
      closedAt[cur] = gen;
      const cx = cur % W, cz = (cur / W) | 0, gCur = seen[cur] === gen ? gScore[cur] as number : Infinity;
      for (let d = 0; d < 8; d++) {
        const dx = DIR_X[d] as number, dz = DIR_Z[d] as number;
        const nx = cx + dx, nz = cz + dz;
        if (nx < 0 || nz < 0 || nx >= W || nz >= H || walk[nz * W + nx] !== 1) continue;
        if (dx && dz && (walk[cz * W + cx + dx] !== 1 || walk[nz * W + cx] !== 1)) continue;  // no corner cutting
        const n = nz * W + nx, ng = gCur + (DIR_COST[d] as number);
        if (ng < (seen[n] === gen ? gScore[n] as number : Infinity)) {
          seen[n] = gen; gScore[n] = ng; came[n] = cur; heap.push(n, ng + hEst(n));
        }
      }
    }
    const cameOf = (c: number) => seen[c] === gen ? came[c] as number : -1;
    if (cameOf(goal) === -1 && goal !== start) return [];
    const cells: number[] = [];
    for (let c = goal; c !== -1 && c !== start; c = cameOf(c)) cells.push(c);
    cells.reverse();
    // string pulling: keep only the corners that line of sight needs
    const pts = cells.map(c => ({ x: this.cx(c % W), z: this.cz((c / W) | 0) }));
    pts[pts.length - 1] = g;
    const out: Point[] = [];
    let from = s, i = 0;
    while (i < pts.length) {
      let j = pts.length - 1;
      while (j > i && !this.lineWalkable(from.x, from.z, (pts[j] as Point).x, (pts[j] as Point).z)) j--;
      out.push(pts[j] as Point);
      from = pts[j] as Point;
      i = j + 1;
    }
    return out;
  }

  private scratchState: Scratch | null = null;

  /* 4-connected labels of walkable cells (0 = blocked). 4-connectivity is exact for findPath: a diagonal
     step is only allowed when both orthogonal neighbours are walkable, so it never joins two regions. */
  private labelRegions(): Int32Array {
    const W = this.w, H = this.h, walk = this.walk, ids = new Int32Array(W * H), stack: number[] = [];
    let next = 0;
    for (let i = 0; i < W * H; i++) {
      if (walk[i] !== 1 || ids[i] !== 0) continue;
      ids[i] = ++next;
      stack.push(i);
      while (stack.length) {
        const c = stack.pop() as number, x = c % W, z = (c / W) | 0;
        if (x > 0 && walk[c - 1] === 1 && ids[c - 1] === 0) { ids[c - 1] = next; stack.push(c - 1); }
        if (x < W - 1 && walk[c + 1] === 1 && ids[c + 1] === 0) { ids[c + 1] = next; stack.push(c + 1); }
        if (z > 0 && walk[c - W] === 1 && ids[c - W] === 0) { ids[c - W] = next; stack.push(c - W); }
        if (z < H - 1 && walk[c + W] === 1 && ids[c + W] === 0) { ids[c + W] = next; stack.push(c + W); }
      }
    }
    return ids;
  }

  private scratch(): Scratch {
    const n = this.w * this.h;
    const sc = this.scratchState ??= {
      gScore: new Float32Array(n), came: new Int32Array(n), seen: new Uint32Array(n), closedAt: new Uint32Array(n),
      gen: 0, heap: new MinHeap(),
    };
    if (sc.gen === 0xFFFFFFFF) { sc.seen.fill(0); sc.closedAt.fill(0); sc.gen = 0; }
    return sc;
  }
}

type Scratch = { gScore: Float32Array; came: Int32Array; seen: Uint32Array; closedAt: Uint32Array; gen: number; heap: MinHeap };

const DIR_X = [1, -1, 0, 0, 1, 1, -1, -1];
const DIR_Z = [0, 0, 1, -1, 1, -1, 1, -1];
const DIR_COST = [1, 1, 1, 1, Math.SQRT2, Math.SQRT2, Math.SQRT2, Math.SQRT2];

/* Binary min-heap over parallel typed arrays. Push/pop order and tie-breaking are exactly those of the
   original number[]-based heap — findPath's output (and so the simulation) depends on them. */
class MinHeap {
  k = new Int32Array(1024);
  p = new Float64Array(1024);
  size = 0;
  clear() { this.size = 0; }
  push(key: number, pri: number) {
    if (this.size === this.k.length) {
      const k = new Int32Array(this.size * 2), p = new Float64Array(this.size * 2);
      k.set(this.k); p.set(this.p);
      this.k = k; this.p = p;
    }
    const k = this.k, p = this.p;
    let i = this.size++;
    k[i] = key; p[i] = pri;
    while (i > 0) {
      const up = (i - 1) >> 1;
      if ((p[up] as number) <= (p[i] as number)) break;
      const tk = k[up] as number, tp = p[up] as number;
      k[up] = k[i] as number; p[up] = p[i] as number; k[i] = tk; p[i] = tp;
      i = up;
    }
  }
  pop(): number {
    const k = this.k, p = this.p, top = k[0] as number, n = --this.size;
    if (n) {
      k[0] = k[n] as number; p[0] = p[n] as number;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = l + 1;
        let m = i;
        if (l < n && (p[l] as number) < (p[m] as number)) m = l;
        if (r < n && (p[r] as number) < (p[m] as number)) m = r;
        if (m === i) break;
        const tk = k[m] as number, tp = p[m] as number;
        k[m] = k[i] as number; p[m] = p[i] as number; k[i] = tk; p[i] = tp;
        i = m;
      }
    }
    return top;
  }
}
